import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Lock, Mail, Pencil, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDate } from "@/lib/api";

export type Notes = Record<string, string>;
export const fmtDob = (v?: string) => (v && /^\d{4}-\d{2}-\d{2}/.test(v) ? `${v.slice(5, 7)}/${v.slice(8, 10)}/${v.slice(0, 4)}` : v || "\u2014");

export function PrivateNote({ id, notes, setNotes }: { id: string; notes: Notes; setNotes: (n: Notes) => void }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const cur = notes[id] || "";
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/dow/private-notes/${id}`, { text }); setNotes({ ...notes, [id]: data.text }); setEdit(false); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (edit) {
    return (
      <div className="w-56 space-y-1">
        <textarea className="field-input min-h-[70px] text-xs" autoFocus maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} data-testid={`private-note-input-${id}`} />
        <div className="flex gap-2">
          <button className="btn-primary btn-sm h-7 px-2 text-xs" disabled={busy} onClick={save} data-testid={`private-note-save-${id}`}>Save</button>
          <button className="text-xs text-[#64748B]" onClick={() => setEdit(false)} data-testid={`private-note-cancel-${id}`}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <button type="button" className="group flex max-w-[10rem] items-start gap-1 text-left text-xs" title="Private note: only you can see this" onClick={() => { setText(cur); setEdit(true); }} data-testid={`private-note-${id}`}>
      <Lock className="mt-0.5 h-3 w-3 shrink-0 text-[#94A3B8]" />
      {cur ? <span className="line-clamp-3 whitespace-pre-wrap text-[#334155]">{cur}</span> : <span className="text-[#94A3B8] group-hover:text-[#1B365D]">Add note</span>}
    </button>
  );
}

export function NameButton({ name, onClick, testId }: { name: string; onClick: () => void; testId: string }) {
  return <button type="button" className="text-left font-semibold text-[#1B365D] hover:underline" onClick={onClick} title="View applicant information" data-testid={testId}>{name || "\u2014"}</button>;
}

export function PersonDialog({ target, onClose, onSaved }: { target: { kind: "case" | "clearance"; id: string; name: string } | null; onClose: () => void; onSaved?: () => void }) {
  const [p, setP] = useState<any>(null);
  const [f, setF] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [charters, setCharters] = useState<any[]>([]);
  useEffect(() => {
    if (target?.kind === "clearance" && !charters.length) { api.get("/dow/charters").then((r) => setCharters(r.data.filter((c: any) => !c.closed))).catch(() => {}); }
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!target) { return; }
    setP(null); setF(null);
    api.get(target.kind === "case" ? `/dow/cases/${target.id}/person` : `/dow/clearances/${target.id}/person`).then((r) => setP(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/dow/clearances/${target!.id}/person`, f);
      setP(data); setF(null); toast.success("Information updated"); onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const row = (label: string, v: React.ReactNode, k: string) => (
    <div className="grid grid-cols-[8.5rem_1fr] items-center gap-2 border-b border-slate-100 py-2 text-sm"><span className="font-semibold text-[#64748B]">{label}</span>
      {f ? <input className="field-input h-9" type={k === "dob" ? "date" : "text"} value={f[FIELD[k]] || ""} onChange={(e) => setF({ ...f, [FIELD[k]]: e.target.value })} data-testid={`person-edit-${k}`} />
        : <span data-testid={`person-${k}`}>{v || "\u2014"}</span>}</div>
  );
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="person-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserRound className="h-5 w-5" /> {p?.name || target?.name}</DialogTitle></DialogHeader>
        {!p ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#94A3B8]" /> : (
          <div>
            {row("First name", p.first_name, "first")}{row("Middle name", p.middle_name, "middle")}{row("Last name", p.last_name, "last")}
            {row("Date of birth", fmtDob(p.dob), "dob")}{row("Gender", p.gender, "gender")}
            {row("Email", p.email && <a className="text-[#1B365D] hover:underline" href={`mailto:${p.email}`}>{p.email}</a>, "email")}
            {row("Phone", p.phone, "phone")}{row("Current address", p.address, "address")}
            {target?.kind === "clearance" && (
              <div className="grid grid-cols-[8.5rem_1fr] items-center gap-2 border-b border-slate-100 py-2 text-sm"><span className="font-semibold text-[#64748B]">Charter</span>
                {f ? <select className="field-input h-9" value={f.charter || ""} onChange={(e) => setF({ ...f, charter: e.target.value })} data-testid="person-edit-charter">
                  <option value="">No charter</option>
                  {charters.map((c) => <option key={c.charter} value={c.charter}>{c.charter} · {c.name}</option>)}
                </select>
                  : <span data-testid="person-charter">{p.charter ? `${p.charter} · ${p.location}` : <span className="font-semibold text-[#B45309]">No charter{p.location ? ` (typed "${p.location}")` : ""}</span>}</span>}</div>
            )}
            {!f && p.previous_addresses?.length > 0 && row("Previous addresses", <ul className="space-y-1">{p.previous_addresses.map((a: any, i: number) => <li key={i}>{a.address} <span className="text-xs text-[#94A3B8]">({a.from} to {a.to})</span></li>)}</ul>, "previous")}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-[#94A3B8]">{f ? "Changes are recorded in the audit log." : "Viewing this information is recorded in the audit log."}</p>
              {p.editable && (f ? <div className="flex gap-2"><button className="btn-secondary btn-sm" onClick={() => setF(null)} data-testid="person-edit-cancel">Cancel</button>
                <button className="btn-primary btn-sm" disabled={busy || !f.first_name?.trim() || !f.last_name?.trim()} onClick={save} data-testid="person-edit-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button></div>
                : <button className="btn-secondary btn-sm" onClick={() => setF({ first_name: p.first_name, middle_name: p.middle_name, last_name: p.last_name, dob: (p.dob || "").slice(0, 10), gender: p.gender, email: p.email, phone: p.phone, address: p.address, charter: p.charter || "" })} data-testid="person-edit-open"><Pencil className="h-4 w-4" /> Edit</button>)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const FIELD: Record<string, string> = { first: "first_name", middle: "middle_name", last: "last_name", dob: "dob", gender: "gender", email: "email", phone: "phone", address: "address" };

const DELIVERY: Record<string, [string, string]> = {
  accepted: ["Accepted for delivery", "text-[#15803D]"], queued: ["Waiting to send", "text-[#B45309]"], failed: ["Not sent", "text-[#B91C1C]"],
};

export function DeliveryList({ rows, testId }: { rows: { to: string[]; at: string; status: string; by?: string }[]; testId: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-3" data-testid={testId}>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#64748B]">Already sent</p>
      <ul className="space-y-1 text-xs">
        {rows.flatMap((h, i) => h.to.map((t) => (
          <li key={`${i}-${t}`} className="flex flex-wrap gap-x-2" data-testid={`${testId}-row`}>
            <span className={`font-semibold ${DELIVERY[h.status][1]}`}>{DELIVERY[h.status][0]}</span>
            <span className="break-all text-[#1A2536]">{t}</span>
            <span className="text-[#64748B]">{new Date(h.at).toLocaleString()}{h.by ? ` · by ${h.by}` : ""}</span>
          </li>
        )))}
      </ul>
    </div>
  );
}

export function EmailCertDialog({ r, onClose, onSent }: { r: any | null; onClose: () => void; onSent: () => void }) {
  const [opts, setOpts] = useState<{ email: string; label: string }[]>([]);
  const [to, setTo] = useState<string[]>([]);
  const [extra, setExtra] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState<any[]>([]);
  useEffect(() => {
    if (!r) { return; }
    setExtra(""); setMsg(""); setOpts([]); setTo([]); setHist([]);
    api.get(`/dow/clearances/${r.id}/email-history`).then(({ data }) => setHist(data)).catch(() => {});
    api.get("/dow/charters").then(({ data }) => {
      const ch = data.find((c: any) => c.charter === r.charter);
      const contacts = (ch?.contacts || []).filter((c: any) => c.email);
      const list = contacts.map((c: any) => ({ email: c.email, label: `${c.name || c.email}${c.coordinator ? " (Compliance Coordinator)" : ""}` }));
      setOpts(list);
      const coords = contacts.filter((c: any) => c.coordinator).map((c: any) => c.email);
      setTo(coords.length ? coords : contacts.map((c: any) => c.email));
    }).catch(() => {});
  }, [r]);
  const all = [...to, ...extra.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean)];
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dow/clearances/${r.id}/email-certificate`, { to: all, message: msg });
      if (data.status === "accepted") toast.success(`Accepted for delivery to ${data.to.join(", ")} at ${new Date(data.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`);
      else toast.info("Queued, will send shortly. The email service is busy, so it will go out automatically in a few minutes.");
      onSent(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto [&>*]:min-w-0" data-testid="email-cert-dialog">
        <DialogHeader><DialogTitle>Email certificate: {r?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#475569]">The Certificate of Clearance is attached as a PDF. Replies come back to you.</p>
        {r?.charter ? <p className="text-xs font-semibold text-[#64748B]">Charter {r.charter} · {r.location}</p> : <p className="text-xs text-[#B45309]">This clearance has no charter, so type the email address below.</p>}
        {hist.length > 0 && <DeliveryList rows={hist} testId="email-cert-history" />}
        <div className="space-y-1.5">{opts.map((o) => (
          <label key={o.email} className="flex min-w-0 items-start gap-2 text-sm"><input type="checkbox" className="mt-1 shrink-0 accent-[#1B365D]" checked={to.includes(o.email)} onChange={(e) => setTo(e.target.checked ? [...to, o.email] : to.filter((x) => x !== o.email))} data-testid={`email-cert-to-${o.email}`} /><span className="min-w-0 break-words">{o.label} <span className="break-all text-xs text-[#94A3B8]">{o.email}</span></span></label>
        ))}</div>
        <label className="block text-sm font-medium">Other email address(es)
          <input className="field-input mt-1" placeholder="name@example.org" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="email-cert-extra" /></label>
        <label className="block text-sm font-medium">Message (optional)
          <textarea className="field-input mt-1 min-h-[80px]" maxLength={3000} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="email-cert-message" /></label>
        <div className="flex justify-end gap-2"><button className="btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy || !all.length} onClick={send} data-testid="email-cert-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send certificate</button></div>
      </DialogContent>
    </Dialog>
  );
}

export function CharterPicker({ charter, location, onChange, testId }: { charter: string; location: string; onChange: (charter: string, location: string) => void; testId: string }) {
  const [list, setList] = useState<any[]>([]);
  useEffect(() => { api.get("/dow/charters").then((r) => setList(r.data.filter((c: any) => !c.closed))).catch(() => {}); }, []);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="block"><span className="mb-1 block text-xs font-semibold text-[#475569]">Charter number</span>
        <select className="field-input" value={charter} onChange={(e) => { const c = list.find((x) => x.charter === e.target.value); onChange(e.target.value, c ? c.name : location); }} data-testid={`${testId}-charter`}>
          <option value="">Choose a charter</option>
          {[...list].sort((a, b) => a.charter.localeCompare(b.charter, undefined, { numeric: true })).map((c) => <option key={c.charter} value={c.charter}>{c.charter}: {c.name}</option>)}
        </select>
      </label>
      <label className="block"><span className="mb-1 block text-xs font-semibold text-[#475569]">Parish / school</span>
        <input className="field-input" value={location} onChange={(e) => onChange(charter, e.target.value)} data-testid={`${testId}-location`} /></label>
    </div>
  );
}

export function DuplicateDialog({ r, onClose, onSaved }: { r: any | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<any>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (r) { setF({ charter: "", location: "", position: r.position || "", issued: new Date().toISOString().slice(0, 10), expires: (r.expires_at || "").slice(0, 10), address: r.address || "" }); }
  }, [r]);
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/dow/clearances/${r.id}/duplicate`, f); toast.success(`Copy created for ${data.location} (${data.number}). It's waiting under Certificates waiting.`); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const F = (label: string, k: string, type = "text") => (
    <label className="block"><span className="mb-1 block text-xs font-semibold text-[#475569]">{label}</span><input className="field-input" type={type} value={f[k] || ""} onChange={set(k)} data-testid={`duplicate-${k}`} /></label>
  );
  return (
    <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="duplicate-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5" /> Duplicate clearance: {r?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#64748B]">Creates a second clearance for another parish or school (copied from {r?.number}). The original stays as it is.</p>
        <CharterPicker charter={f.charter || ""} location={f.location || ""} onChange={(charter, location) => setF({ ...f, charter, location })} testId="duplicate" />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">{F("Ministry / position", "position")}</div>
          {F("Issued", "issued", "date")}{F("Expires (blank = no expiration)", "expires", "date")}
          <div className="sm:col-span-2">{F("Address", "address")}</div>
        </div>
        <div className="flex justify-end gap-2"><button className="btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy || !f.issued || (!f.charter && !f.location?.trim())} onClick={save} data-testid="duplicate-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create copy</button></div>
      </DialogContent>
    </Dialog>
  );
}

export function PositionDialog({ r, onClose, onSaved }: { r: any | null; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (r) { setV(r.position || ""); } }, [r]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/dow/clearances/${r.id}/position`, { position: v }); toast.success("Ministry / position updated. The certificate shows the new text."); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="position-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4" /> Ministry / position: {r?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#64748B]">This text prints on the certificate, so the Compliance Coordinator sees it.</p>
        <textarea className="field-input min-h-[90px]" maxLength={300} value={v} onChange={(e) => setV(e.target.value)} data-testid="position-input" />
        {r?.position_original !== undefined && r.position_original !== v && <p className="text-xs text-[#94A3B8]" data-testid="position-original">Applicant's original answer: {r.position_original || "(blank)"}</p>}
        <div className="flex justify-end gap-2"><button className="btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy} onClick={save} data-testid="position-save">Save</button></div>
      </DialogContent>
    </Dialog>
  );
}

export function SentEmails() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [open, setOpen] = useState<any>(null);
  const [waiting, setWaiting] = useState<any[]>([]);
  const loadQ = () => api.get("/dow/email-queue").then((r) => setWaiting(r.data)).catch(() => setWaiting([]));
  useEffect(() => { api.get("/dow/sent-emails").then((r) => setRows(r.data)).catch(() => setRows([])); loadQ(); }, []);
  const retry = (id: string) => api.post(`/dow/email-queue/${id}/retry`).then(() => { toast.success("Trying again now"); loadQ(); }).catch((e) => toast.error(errMsg(e)));
  const view = (id: string) => api.get(`/dow/sent-emails/${id}`).then((r) => setOpen(r.data)).catch((e) => toast.error(errMsg(e)));
  if (rows === null) return <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-[#94A3B8]" />;
  return (
    <div className="p-1">
      <p className="mb-3 text-xs text-[#64748B]">Every email you send from this dashboard (certificates, lists, reminders, application links, notices). Only you can see this list.</p>
      {waiting.length > 0 && (
        <div className="mb-4 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-3" data-testid="dow-email-waiting">
          <p className="mb-2 text-sm font-semibold text-[#92400E]">Waiting to send ({waiting.length}). These go out automatically when the email service is free.</p>
          <ul className="space-y-1.5">
            {waiting.map((w) => (
              <li key={w.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`dow-email-waiting-row-${w.id}`}>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${w.status === "failed" ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#FEF3C7] text-[#B45309]"}`} data-testid={`dow-email-waiting-status-${w.id}`}>{w.status === "failed" ? "Not sent" : "Waiting"}</span>
                <span className="min-w-0 flex-1 basis-48"><span className="font-medium text-[#1A2536]">{w.subject}</span> <span className="text-xs text-[#64748B]">to {w.to.join(", ")} · queued {new Date(w.created_at).toLocaleString()}</span></span>
                <button className="btn-secondary btn-sm" onClick={() => retry(w.id)} data-testid={`dow-email-waiting-retry-${w.id}`}>Try again now</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!rows.length ? <p className="py-6 text-center text-sm text-[#94A3B8]" data-testid="sent-emails-empty">No emails sent yet.</p> : (
        <div className="max-h-[420px] overflow-auto">
          <table className="tbl" data-testid="sent-emails-table">
            <thead><tr><th>Sent</th><th>To</th><th>Subject</th><th>Attachments</th><th>Delivery</th><th /></tr></thead>
            <tbody>{rows.map((e) => (
              <tr key={e.id} data-testid={`sent-email-row-${e.id}`}>
                <td className="whitespace-nowrap font-mono text-xs">{new Date(e.at).toLocaleString()}</td>
                <td className="text-xs">{e.to.join(", ")}</td>
                <td className="text-sm font-medium">{e.subject}</td>
                <td className="text-xs text-[#64748B]">{e.attachments?.join(", ") || "\u2014"}</td>
                <td className="whitespace-nowrap text-xs font-semibold text-[#15803D]" data-testid={`sent-email-delivery-${e.id}`} title="The email service accepted this email for every recipient. It can't confirm inbox delivery or bounces.">Accepted for delivery</td>
                <td><button className="btn-secondary btn-sm" onClick={() => view(e.id)} data-testid={`sent-email-view-${e.id}`}>View</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl" data-testid="sent-email-dialog">
          <DialogHeader><DialogTitle>{open?.subject}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-1 text-sm">
              <p><b>Sent:</b> {new Date(open.at).toLocaleString()} ({fmtDate(open.at)})</p>
              <p data-testid="sent-email-delivery-status"><b>Delivery:</b> <span className="font-semibold text-[#15803D]">Accepted for delivery</span> to {open.to.length} recipient(s) at {new Date(open.at).toLocaleTimeString()}</p>
              <p><b>From:</b> {open.from_name}{open.reply_to ? ` (replies to ${open.reply_to})` : ""}</p>
              <p data-testid="sent-email-to"><b>To:</b> {open.to.join(", ")}</p>
              {open.attachments?.length > 0 && <p><b>Attachments:</b> {open.attachments.join(", ")}</p>}
              <iframe title="Email message" sandbox="allow-same-origin" srcDoc={open.html} className="mt-2 h-[50vh] w-full rounded-lg border border-slate-200 bg-white" data-testid="sent-email-body" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
